import { ApplicationRef, inject, Injectable } from '@angular/core';
import { SwUpdate, VersionEvent } from '@angular/service-worker';
import { concat, first, interval, Observable } from 'rxjs';
import { Platform, ToastController } from '@ionic/angular/standalone';
import { environment } from 'src/environments/environment';
import * as Sentry from '@sentry/angular';

const CACHE_INTEGRITY_KEY: string = 'cache-integrity-retries';
const INTEGRITY_RELOADING_KEY: string = 'cache-integrity-reloading';
const FORCE_RELOAD_KEY: string = 'pwa-force-reload';
const MAX_INTEGRITY_RETRIES: number = 2;

interface NgswManifest {
  assetGroups?: NgswAssetGroup[];
}

interface NgswAssetGroup {
  name: string;
  installMode: string;
  urls: string[];
}

@Injectable({
  providedIn: 'root',
})
export class PwaUpdateService {
  private readonly swUpdate: SwUpdate = inject(SwUpdate);
  private readonly appRef: ApplicationRef = inject(ApplicationRef);
  private readonly platform: Platform = inject(Platform);
  private readonly toastController: ToastController = inject(ToastController);

  private readonly updateCheckInterval: number = 300000;
  private isUpdateInstalling: boolean = false;

  constructor() {
    if (this.swUpdate.isEnabled) {
      this.initializeUpdateChecks();
      this.listenForUpdates();
    }
  }

  private initializeUpdateChecks(): void {
    const appIsStable$: Observable<boolean> = this.appRef.isStable.pipe(
      first((isStable: boolean) => isStable === true)
    );
    const everyFiveMinutes$: Observable<number> = interval(this.updateCheckInterval);
    const everyFiveMinutesOnceAppIsStable$: Observable<boolean | number> = concat(appIsStable$, everyFiveMinutes$);

    everyFiveMinutesOnceAppIsStable$.subscribe(async () => {
      try {
        await this.swUpdate.checkForUpdate();
      } catch {
        // Periodic check failure is non-critical
      }
    });

    if (this.platform.is('capacitor') || this.platform.is('cordova')) {
      this.platform.resume.subscribe(async () => {
        try {
          await this.swUpdate.checkForUpdate();
        } catch {
          // Update check failure is non-critical
        }
        try {
          await this.verifyCacheIntegrity();
        } catch {
          // Integrity check failure is non-critical
        }
      });
    }

    document.addEventListener('visibilitychange', async () => {
      if (!document.hidden) {
        try {
          await this.swUpdate.checkForUpdate();
        } catch {
          // Update check failure is non-critical
        }
        try {
          await this.verifyCacheIntegrity();
        } catch {
          // Integrity check failure is non-critical
        }
      }
    });
  }

  private listenForUpdates(): void {
    this.swUpdate.versionUpdates.subscribe((evt: VersionEvent) => {
      if (evt.type === 'VERSION_DETECTED') {
        this.isUpdateInstalling = true;
      } else if (evt.type === 'VERSION_READY') {
        this.forceUpdate();
      } else if (evt.type === 'VERSION_INSTALLATION_FAILED') {
        this.isUpdateInstalling = false;
      }
    });

    this.swUpdate.unrecoverable.subscribe((event) => {
      Sentry.captureException(new Error(`SW unrecoverable: ${event.reason}`), {
        tags: { swUnrecoverable: true },
      });
      this.clearCacheAndReload();
    });
  }

  private async forceUpdate(): Promise<void> {
    const toast: HTMLIonToastElement = await this.toastController.create({
      message: 'Updating to latest version...',
      duration: 1500,
      position: 'top',
      color: 'primary',
    });
    await toast.present();

    await this.activateUpdate();
  }

  private async activateUpdate(): Promise<void> {
    try {
      const controllerChanged: Promise<void> = new Promise<void>((resolve) => {
        navigator.serviceWorker.addEventListener(
          'controllerchange',
          () => resolve(),
          { once: true },
        );
      });

      const controllerTimeout: Promise<void> = new Promise<void>((resolve) => setTimeout(resolve, 5000));

      await this.swUpdate.activateUpdate();
      await Promise.race([controllerChanged, controllerTimeout]);

      const cacheNames: string[] = await caches.keys();
      await Promise.all(cacheNames.map((name: string) => caches.delete(name)));

      sessionStorage.setItem(FORCE_RELOAD_KEY, 'true');
      window.location.reload();
    } catch {
      await this.clearCacheAndReload();
    }
  }

  async checkForUpdate(): Promise<void> {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    try {
      const updateFound: boolean = await this.swUpdate.checkForUpdate();
      if (!updateFound) {
        const reloadTriggered: boolean = await this.verifyCacheIntegrity();
        if (!reloadTriggered) {
          const toast: HTMLIonToastElement = await this.toastController.create({
            message: 'You are on the latest version',
            duration: 2000,
            position: 'bottom',
            color: 'success',
          });
          await toast.present();
        }
      }
    } catch {
      const toast: HTMLIonToastElement = await this.toastController.create({
        message: 'Failed to check for updates',
        duration: 2000,
        position: 'bottom',
        color: 'danger',
      });
      await toast.present();
    }
  }

  async verifyCacheIntegrity(): Promise<boolean> {
    if (!this.swUpdate.isEnabled) return false;
    if (this.isUpdateInstalling) return false;

    if (sessionStorage.getItem(FORCE_RELOAD_KEY)) {
      sessionStorage.removeItem(FORCE_RELOAD_KEY);
      return false;
    }

    if (!navigator.serviceWorker.controller) return false;

    const retries: number = this.getIntegrityRetryCount();
    if (retries >= MAX_INTEGRITY_RETRIES) return false;

    try {
      const response: Response = await fetch('/ngsw.json?integrity-check=' + Date.now());
      if (!response.ok) return false;

      const manifest: NgswManifest = await response.json();
      const prefetchGroup: NgswAssetGroup | undefined = manifest.assetGroups?.find(
        (group: NgswAssetGroup) => group.installMode === 'prefetch',
      );
      if (!prefetchGroup?.urls?.length) return false;

      const expectedCount: number = prefetchGroup.urls.length;
      const allCacheNames: string[] = await caches.keys();
      const ngswCaches: string[] = allCacheNames.filter(
        (name: string) => name.startsWith('ngsw:') && name.includes(`:assets:${prefetchGroup.name}:`),
      );

      if (ngswCaches.length === 0) return false;

      let cachedCount: number = 0;
      for (const cacheName of ngswCaches) {
        const cache: Cache = await caches.open(cacheName);
        const keys: readonly Request[] = await cache.keys();
        cachedCount += keys.length;
      }

      const completionRatio: number = expectedCount > 0 ? cachedCount / expectedCount : 1;

      if (completionRatio < 0.5) {
        Sentry.captureException(
          new Error(`SW cache incomplete: ${cachedCount}/${expectedCount} assets cached`),
          { tags: { cacheIntegrity: true, completionRatio } },
        );
        this.setIntegrityRetryCount(retries + 1);
        sessionStorage.setItem(INTEGRITY_RELOADING_KEY, 'true');
        await this.clearCacheAndReload();
        return true;
      }
    } catch {
      // Integrity check failure is non-critical
    }

    return false;
  }

  async clearCacheAndReload(): Promise<void> {
    try {
      const cacheNames: string[] = await caches.keys();
      await Promise.all(cacheNames.map((cacheName: string) => caches.delete(cacheName)));

      const registrations: readonly ServiceWorkerRegistration[] =
        await navigator.serviceWorker.getRegistrations();
      await Promise.all(
        registrations.map((registration: ServiceWorkerRegistration) => registration.unregister()),
      );

      const url: URL = new URL(window.location.href);
      url.searchParams.set('cache-bust', Date.now().toString());
      window.location.href = url.toString();
    } catch {
      window.location.reload();
    }
  }

  getCurrentVersion(): string {
    return environment.appVersion || 'unknown';
  }

  private getIntegrityRetryCount(): number {
    const value: string | null = sessionStorage.getItem(CACHE_INTEGRITY_KEY);
    return value ? parseInt(value, 10) : 0;
  }

  private setIntegrityRetryCount(count: number): void {
    sessionStorage.setItem(CACHE_INTEGRITY_KEY, count.toString());
  }

  static resetIntegrityRetryCount(): void {
    if (sessionStorage.getItem(INTEGRITY_RELOADING_KEY)) {
      sessionStorage.removeItem(INTEGRITY_RELOADING_KEY);
      return;
    }
    sessionStorage.removeItem(CACHE_INTEGRITY_KEY);
  }
}
